import BacktestLoader from './components/BacktestLoader';
import BacktestKpis from './components/BacktestKpis';
import EquityChart from './components/EquityChart';

export default function App() {
  return (
    <div className="page">
      <BacktestLoader />

      {/* HERO */}
      <section className="heroFull">
        <div className="inner">
          <header className="hero heroWide">
            <div className="heroTop">
              <div className="brandPill">SPX Gamma</div>
              <div className="heroMeta">MEIC • 0-DTE • Multiple Entry • Income-Focused</div>
            </div>

            <h1 className="heroTitle">MEIC — Multiple Entry Iron Condor</h1>

            <p className="heroSubtitle">
              A systematic 0-DTE SPX iron condor framework designed for larger portfolios seeking repeatable intraday premium-selling opportunities.
            </p>

            <div className="heroBody">
              <p className="heroLead">
                <strong>MEIC (Multiple Entry Iron Condor)</strong> is the 0-DTE income engine inside the broader{' '}
                <strong>SPX Gamma Premium Sellers strategy suite</strong>. While ACCE and ARGOS are designed to help build and
                grow portfolio capital, and GEMIC provides the core daily 1-DTE expected-move structure inside the suite, MEIC represents the more capitalized stage of the system: a structured approach
                for deploying larger account sizes into systematic 0-DTE SPX iron condors.
              </p>

              <div style={{ height: 5 }} />

              <p className="heroLead">
                MEIC is designed for traders with larger portfolios because the strategy requires meaningful capital to operate
                properly. Conceptually, the framework is built around a minimum daily capital base of approximately{' '}
                <strong>$30,000</strong>, allowing traders to run multiple defined-risk iron condor entries throughout the
                session while maintaining appropriate risk control and position sizing discipline.
              </p>

              <div style={{ height: 5 }} />

              <p className="heroLead">
                The goal of MEIC is not aggressive account acceleration. The goal is systematic premium collection. Once a
                trader has developed enough capital through the broader suite, MEIC becomes the end-state strategy: a repeatable
                intraday process for selling defined-risk 0-DTE SPX iron condors when market structure, volatility behavior,
                and price movement support premium-selling conditions.
              </p>

              <div style={{ height: 5 }} />

              <p className="heroLead">
                Each MEIC position is built around defined risk, multiple intraday entry opportunities, and disciplined
                execution. Rather than relying on one single trade idea, the framework is designed to identify multiple
                opportunities during the trading session, allowing capital to be deployed systematically while keeping risk
                contained by the structure of the iron condor.
              </p>
            </div>

            <div className="heroActions">
              <a className="btnPrimary" href="#performance">View Performance</a>
              <a
                className="btnGhost"
                href="https://www.spxgamma.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Access SPX Gamma
              </a>
            </div>
          </header>
        </div>
      </section>

      {/* EQUITY / PERFORMANCE FIRST */}
      <section id="performance" className="sectionFull">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Equity Curve</h2>
            <p className="sectionDesc">Performance: 1 Contract</p>
          </div>

          <EquityChart />
        </div>
      </section>

      {/* STRATEGY OBJECTIVE + HOW IT WORKS */}
      <section className="sectionFull">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Strategy Objective</h2>
            <p className="sectionDesc">
              Compound capital steadily while maintaining tight drawdown control across market regimes.
            </p>
          </div>

          <div className="card">
            <ul className="bullets">
              <li>High-probability intraday setups</li>
              <li>Defined-risk option structures</li>
              <li>Strict regime and volatility filters</li>
              <li>Disciplined trade management</li>
            </ul>
          </div>

          <div className="sectionHeader" style={{ marginTop: 18 }}>
            <h2 className="sectionTitle">How the Strategy Works</h2>
            <p className="sectionDesc">Three pillars: regime context, construction, and risk management.</p>
          </div>

          <div className="stack">
            <div className="card">
              <div className="stepTitle">1️⃣ Market Regime Identification</div>

              <p className="text">
                Each trading session is classified using gamma exposure (GEX) and volatility behavior,
                defining the prevailing market regime and the structural conditions under which price
                action tends to evolve. Different regimes exhibit distinct price dynamics, risk profiles,
                and responses to intraday option flows.
              </p>

              <div style={{ height: 10 }} />

              <p className="text">
                The SPX Gamma MEIC Framework uses this regime structure to adjust entry timing and participation
                rules across sessions, balancing risk containment with opportunity efficiency. Trades are
                evaluated only when both the identified regime and 0-DTE option flows align to support a
                favorable structural edge, reinforcing consistency across varying market environments.
              </p>
            </div>

            <div className="card">
              <div className="stepTitle">2️⃣ Defined-Risk Trade Construction</div>
              <p className="text">
                Positions are constructed using risk-defined option spreads so that losses are capped and capital
                at risk is known in advance. Trade construction follows a consistent, rules-based process: each
                trading day, six iron condors are identified—comprising a total of twelve option legs—by pairing
                the farthest out-of-the-money call and put spreads that are 50 points wide and offer a minimum credit
                of $1.80. The framework is designed around a daily capital allocation of approximately $30,000, with
                trade sizing and structure standardized to preserve statistical integrity and maintain consistency
                across varying market regimes.
              </p>
            </div>

            <div className="card">
              <div className="stepTitle">3️⃣ Rule-Based Trade Management</div>
              <p className="text">
                Trades are managed using predefined exits and risk thresholds to remove discretion and emotional
                decision-making. Positions are held to expiration unless a stop condition is reached, which occurs
                when the Mark value of either the call or put credit spread exceeds the original credit received
                for that spread, defining a 1× stop-loss framework. The approach does not rely on oversized winners,
                market prediction, or leverage, emphasizing consistency and disciplined risk containment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DIFFERENTIATION */}
      <section className="sectionFull">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Why This Strategy Is Different</h2>
            <p className="sectionDesc">Structure-driven execution with risk-first constraints.</p>
          </div>

          <div className="grid2">
            <div className="card">
              <div className="cardTitleSm">Why strategies fail</div>
              <p className="text">
                Many trading strategies fail not because of poor execution, but because they ignore underlying
                market structure, concentrate risk excessively, and rely on directional prediction rather than
                probabilistic frameworks. Without accounting for how price behavior changes across regimes or
                how risk compounds under stress, even well-intentioned strategies can break down when market
                regimes shift.
              </p>
            </div>

            <div className="card">
              <div className="cardTitleSm">Why MEIC works</div>
              <p className="text">
                The MEIC framework is built around market regime structure rather than price prediction, allowing
                positioning logic to adapt as underlying conditions change. By anchoring decisions to probabilistic
                behavior across regimes—and prioritizing defined risk over return targets—the framework maintains
                consistency through varying volatility environments while avoiding dependence on narrative-driven
                assumptions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="sectionFull">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Who This Framework Is For</h2>
            <p className="sectionDesc">
              Designed for well-capitalized traders who want a systematic 0-DTE SPX premium-selling process.
            </p>
          </div>

          <div className="card">
            <p className="text">
              The <strong>SPX Gamma MEIC Framework</strong> is designed for traders who have already developed a larger
              capital base and are ready to apply a structured approach to intraday 0-DTE premium selling. Conceptually,
              MEIC is the income-focused stage of the SPX Gamma premium-selling suite, requiring approximately
              <strong> $30,000 in daily deployable capital</strong> to operate the strategy with appropriate risk control.
            </p>

            <div style={{ height: 10 }} />

            <p className="text">
              Inside the broader suite, ACCE and ARGOS are intended to help traders grow and develop portfolio capital,
              while GEMIC provides the core daily 1-DTE expected-move structure. MEIC is the more capitalized framework:
              a process for traders who have reached the point where systematic 0-DTE income generation becomes the primary
              objective.
            </p>

            <div className="callout">
              MEIC is built around a full-size deployment model, but participation can be scaled. The key is not forcing the full
              structure before the account is ready. Traders can begin with selected entry windows, smaller deployment, and the same
              regime-aware framework, then expand toward the full MEIC structure as capital grows.
            </div>
          </div>
        </div>
      </section>

      {/* CAPITAL / ENTRY-TIME SCALING */}
      <section className="sectionFull">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Scaling the Framework for Smaller Accounts</h2>
            <p className="sectionDesc">
              Regime-aware timing allows participation to scale without deploying the full six-condor structure.
            </p>
          </div>

          <div className="grid2">
            <div className="card">
              <div className="cardTitleSm">Scaling the Framework</div>

              <p className="text">
                Full MEIC deployment targets six iron condors per day (12 total legs) and typically requires
                approximately $30,000 in daily capital. Not every trader needs—or wants—to deploy the full structure.
              </p>

              <div style={{ height: 10 }} />

              <p className="text">
                Because price behavior and trade outcomes vary across market regimes, MEIC’s entry schedule adapts
                accordingly. Each potential entry is evaluated using both regime context and real-time 0-DTE option
                flows to determine structural suitability, allowing the framework to participate selectively rather
                than forcing trades.
              </p>

              <div style={{ height: 10 }} />

              <p className="text">
                Traders with smaller portfolios can scale participation by focusing on the entry windows that have
                historically shown stronger outcomes for each regime, while applying the same flow-based filters.
                This reduces capital usage and trade frequency while preserving alignment with regime-aware timing.
              </p>

              <div className="callout">
                Disclaimer: This material is provided for educational and informational purposes only. It is intended to
                illustrate how market regime context and 0-DTE option flow analysis can be used to refine and evaluate
                established 0-DTE options strategies.
              </div>
            </div>

            <div className="card">
              <div className="cardHeader">
                <div className="cardTitle">Regime 0 / 1</div>
                <div className="cardTag">Best: 9:45 AM & 2:15 PM</div>
              </div>

              <table className="dataTable">
                <thead>
                  <tr>
                    <th>Entry</th>
                    <th>Trades</th>
                    <th>Wins</th>
                    <th>Win Rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>9:33</td><td>680</td><td>374</td><td>55.00%</td></tr>
                  <tr className="bestRow"><td className="bestCell">9:45 ⭐</td><td>680</td><td>383</td><td className="bestCell">56.32%</td></tr>
                  <tr><td>10:00</td><td>680</td><td>375</td><td>55.15%</td></tr>
                  <tr><td>10:15</td><td>680</td><td>377</td><td>55.44%</td></tr>
                  <tr><td>10:45</td><td>680</td><td>376</td><td>55.29%</td></tr>
                  <tr className="bestRow"><td className="bestCell">2:15 ⭐</td><td>676</td><td>383</td><td className="bestCell">56.66%</td></tr>
                </tbody>
              </table>
            </div>

            <div className="card">
              <div className="cardHeader">
                <div className="cardTitle">Regime 2</div>
                <div className="cardTag">Best: 1:00 & 1:45 PM</div>
              </div>

              <table className="dataTable">
                <thead>
                  <tr>
                    <th>Entry</th>
                    <th>Trades</th>
                    <th>Wins</th>
                    <th>Win Rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>12:30</td><td>212</td><td>119</td><td>56.13%</td></tr>
                  <tr><td>12:45</td><td>212</td><td>124</td><td>58.49%</td></tr>
                  <tr className="bestRow"><td className="bestCell">1:00 ⭐</td><td>212</td><td>126</td><td className="bestCell">59.43%</td></tr>
                  <tr><td>1:15</td><td>212</td><td>122</td><td>57.5%</td></tr>
                  <tr className="bestRow"><td className="bestCell">1:45 ⭐</td><td>212</td><td>126</td><td className="bestCell">59.43%</td></tr>
                  <tr><td>2:00</td><td>212</td><td>125</td><td>58.96%</td></tr>
                </tbody>
              </table>
            </div>

            <div className="card">
              <div className="cardHeader">
                <div className="cardTitle">Regime 3</div>
                <div className="cardTag">Best: 1:00, 2:00 & 2:30 PM</div>
              </div>

              <table className="dataTable">
                <thead>
                  <tr>
                    <th>Entry</th>
                    <th>Trades</th>
                    <th>Wins</th>
                    <th>Win Rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bestRow"><td className="bestCell">1:00 ⭐</td><td>352</td><td>202</td><td className="bestCell">57.39%</td></tr>
                  <tr><td>1:30</td><td>352</td><td>198</td><td>56.25%</td></tr>
                  <tr className="bestRow"><td className="bestCell">2:00 ⭐</td><td>352</td><td>202</td><td className="bestCell">57.39%</td></tr>
                  <tr><td>2:15</td><td>352</td><td>200</td><td>56.82%</td></tr>
                  <tr className="bestRow"><td className="bestCell">2:30 ⭐</td><td>352</td><td>204</td><td className="bestCell">57.90%</td></tr>
                  <tr><td>2:45</td><td>352</td><td>199</td><td>56.53%</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* PERFORMANCE SUMMARY BELOW */}
      <section className="sectionFull">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Performance Summary (2023 → Present)</h2>
            <p className="sectionDesc">
              {/* Live backtest statistics + equity curve loaded from your database via WSS. */}
            </p>
          </div>

          <div className="card">
            <div className="cardHeader">
              <div className="cardTitle">Key Metrics</div>
              <div className="cardTag">Full Data Set</div>
            </div>
            <BacktestKpis />
          </div>
        </div>
      </section>

      {/* FINAL TAKEAWAY */}
      <section className="sectionFull">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Final Takeaway</h2>
            <p className="sectionDesc">
              The capitalized 0-DTE income layer of the SPX Gamma Premium Sellers suite.
            </p>
          </div>

          <div className="card">
            <p className="text">
              The <strong>SPX Gamma MEIC Framework</strong> is the full-scale 0-DTE premium-selling component of the broader{' '}
              <strong>SPX Gamma Premium Sellers suite</strong>. It is designed for traders who have developed enough capital to
              move beyond occasional participation and apply a systematic, repeatable approach to intraday SPX iron condor
              trading.
            </p>

            <div style={{ height: 10 }} />

            <p className="text">
              MEIC uses regime context, dealer positioning, volatility structure, and real-time 0-DTE option flow to determine
              when multiple intraday iron condor entries are structurally justified. The goal is not to predict every SPX move or
              force trades throughout the session. The goal is to deploy capital only when the market environment supports
              defined-risk premium collection.
            </p>

            <div style={{ height: 10 }} />

            <p className="text">
              Full MEIC deployment is designed around a larger capital base and multiple potential intraday entries. Traders can
              scale into the framework by focusing on selected entry windows first, but the long-term role of MEIC is clear: it is
              the more capitalized income engine of the suite, built for consistent 0-DTE premium generation once the account can
              support the full structure.
            </p>

            <div className="callout">
              MEIC represents the end-state premium-selling framework inside SPX Gamma: larger capital deployment, multiple
              intraday entries, defined-risk iron condors, regime-aware timing, and systematic 0-DTE income generation.
            </div>

            <div className="heroActions" style={{ marginTop: 14 }}>
              <a
                className="btnPrimary"
                href="https://spxgamma-spx-premium-sellers-dashboard.spxgamma.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Access the MEIC Dashboard
              </a>
              <div className="figureDisclaimer" style={{ marginTop: 16 }}>
              MEIC trade posts are for educational and informational purposes only. They are not
              financial advice or a recommendation to buy or sell any security or options contract.
              Options trading involves risk and may not be suitable for all traders. Members are
              responsible for their own trade decisions, position sizing, and risk management.
            </div>
              <footer className="footer" style={{ marginTop: 18 }}>
            <div className="footerLeft">© {new Date().getFullYear()} SPX Gamma</div>
            <div className="footerRight">Data-driven. Regime-aware. Risk-controlled.</div>
          </footer>
            </div>
          </div>
        </div>
      </section>


    </div>
  );
}






